/**
 * The one GameState (v0.2 RFC §7) — plain JSON-serializable data, account/run
 * split (ADR-003). SCHEMA 2: tiers map, availableTiers, alumni, travel-era
 * counters. No class instances, no Phaser objects.
 */

import {
  AUTOMATOR_ACTIONS_BY_LEVEL,
  CARE_TIERS,
  DISCHARGE,
  ECONOMY,
  MAILBOX,
  OFFICE,
  TIERS,
  type CareTierId,
  type DischargePolicy,
} from "@/data/tunables";
import { newRngState, type RngState } from "@/systems/rng";
import type { CardScene } from "@/data/cards";
import {
  prelevelAutomator,
  seedFundingBonus,
  startsCriminalUnlocked,
  veteranRecruiterLevel,
  type UpgradeId,
} from "@/data/upgrades";
import type { CriminalUpgradeId } from "@/data/criminal";

export type PatientState = "new" | "progressing" | "issue" | "paused";

/** Every unlockable rung, in order; "alumni" opens the network (the pool). */
export type TierId = CareTierId | "alumni";

export interface Lead {
  id: number;
  name: string;
  /** Present when the lead is a returning ex-patient/alumnus (M8 memory). */
  issueHistory?: number;
  relapseCount?: number;
}

export interface Patient {
  id: number;
  name: string;
  tier: CareTierId;
  state: PatientState;
  /** 0..100 — HARD invariant, every update clamps. */
  progress: number;
  /** Issues fired on this patient — the M8 undertreat memory. */
  issueHistory: number;
  relapseCount: number;
  /** Slot index within the tier floor. */
  bed: number;
}

export interface Alumnus {
  id: number;
  name: string;
  /** Trips through the ladder (for flavor + Frequent Flyer later). */
  relapseCount: number;
  issueHistory: number;
}

export interface TierState {
  automatorLevel: number;
  dischargePolicy: DischargePolicy;
}

export interface AccountState {
  facilityName: string | null;
  /** Deepest rung ever reached; ratchets forward only. */
  highWaterLegit: "none" | TierId;
  highWaterCriminal: number;
  reputationCapital: number;
  streetKnowledge: number;
  /** Lifetime clean graduations (prestige scaling v0.5, letters v0.3). */
  lifetimeGraduations: number;
  /** Permanent prestige-shop levels (v0.4); absent id = level 0. */
  upgrades: Partial<Record<UpgradeId, number>>;
}

export interface MailboxInstance {
  id: string;
  arrivedTick: number;
  read: boolean;
}

/** One spawned, still-collectible loot drop (v0.13). */
export interface ActiveLootEvent {
  id: number;
  kind: "money" | "contraband";
  tier: CareTierId;
  /** Display item (sprite/name key), e.g. "wallet", "baggie". */
  item: string;
  /** Funding value BEFORE contraband multipliers (applied at collect). */
  value: number;
  /** Remaining LIVE ticks before it despawns (frozen offline). */
  lifeLeft: number;
}

/** One open choice card (v0.14). Content is `defId` into data/cards. */
export interface ActiveCard {
  id: number;
  defId: string;
  scene: CardScene;
  /** Remaining LIVE ticks before the passive outcome auto-fires. */
  ticksLeft: number;
}

/** v0.13 scheduler state — plain data, in the save. */
export interface EventsState {
  /** The scheduler's OWN seeded stream (isolated from run.rng). */
  rng: RngState;
  /** Monotonic id source for spawned events. */
  seq: number;
  active: ActiveLootEvent[];
  /** Open choice cards (v0.14) — at most one per scene. */
  cards: ActiveCard[];
  /** Remaining LIVE ticks until each channel may roll again. */
  cooldownLeft: Record<string, number>;
  /** Satire rotation: indices not yet shown this cycle (seeded shuffle). */
  satireDeck: number[];
}

/** Fresh v0.13 scheduler state for a run seed. */
export function newEventsState(runSeed: number): EventsState {
  return {
    rng: newRngState((runSeed ^ 0x9e3779b9) >>> 0),
    seq: 0,
    active: [],
    cards: [],
    cooldownLeft: {},
    satireDeck: [],
  };
}

export interface RunState {
  tick: number;
  rng: RngState;
  funding: number;
  reputation: number;
  heat: number;
  dea: number;
  office: { recruiterLevel: number };
  queue: Lead[];
  /** Ordered list of OPEN rungs; the last care tier in it is the frontier. */
  availableTiers: TierId[];
  /** Per-care-tier automator/policy config (patients live in `patients`). */
  tiers: Record<CareTierId, TierState>;
  /** All in-care patients, across every tier (each carries its tier). */
  patients: Patient[];
  alumni: {
    roster: Alumnus[];
    /** Contact-call boost active until this tick. */
    contactBoostUntil: number;
    coordinatorLevel: number;
    /** Fractional reputation accumulator (rep itself stays integral). */
    repFraction: number;
  };
  /** Graduates who exited the world (frontier < full continuum) — invisible
   *  counter per the ruling ("outcome data unavailable"). */
  graduatedOut: number;
  /** Completions per tier this run (offer-letter requirements read these). */
  graduationsByTier: Record<CareTierId, number>;
  /** Cumulative counters (survive patient removal) — Incident-letter inputs. */
  issuesEver: number;
  deathsEver: number;
  /** The mailbox (v0.3): arrived instances; defs live in src/data/letters. */
  mailbox: { arrived: MailboxInstance[]; pending: string[]; lastArrivalTick: number };
  /** Letter-granted capacity raises (effectiveCapacity applies them). */
  capacityBonus: Record<CareTierId, number>;
  capacityMultiplier: Record<CareTierId, number>;
  /** High-water mark at RUN START — freezes expansion eligibility (§10.3). */
  markAtRunStart: "none" | TierId;
  /** v0.13 event scheduler state (RFC §3.5) — own rng stream so event
   *  rolls can't perturb main-sim determinism; ONLINE-ONLY counters. */
  events: EventsState;
  /** Set true when the "Sell the Facility" letter is read; gates prestige (v0.4). */
  prestigeOffered: boolean;
  /** Highest net/sec observed this run — a prestige-payout input (v0.4). */
  peakNetPerSec: number;
  /** v0.5 "Consequences": body broker + mitigation staff + death-letter debt. */
  brokerUnlocked: boolean;
  /** Tick until which the broker is locked out (0 = ready) — v0.10 M3. */
  brokerLockoutUntil: number;
  /** Cumulative leads sold to the broker — feeds the relapse formula. */
  brokerHistory: number;
  /** Mitigation staff levels (reputation / heat / DEA). */
  prFirmLevel: number;
  complianceOfficerLevel: number;
  /** v0.15 "The Fixer": the DEA-axis mitigation role. Upkeep-salaried — a
   *  per-tick bribe scaling with the DEA it removes (see systems/meters). */
  fixerLevel: number;
  /** Unresolved deaths awaiting a Death Letter (batched; scales its cost). */
  deathsPending: number;
  /** v0.6 "The Other Business": the criminal track (run-level; access ratchets). */
  criminal: {
    unlocked: boolean;
    /** Highest criminal tier the player may buy from (0 = none, 1..5). */
    tierAccess: number;
    /** Owned criminal upgrade levels (reset each run; absent id = 0). */
    upgrades: Partial<Record<CriminalUpgradeId, number>>;
  };
  /** Set by reading an expansion offer; cleared by the purchase. */
  purchasable: { tier: TierId; price: number } | null;
  incomeLog: Array<{ tick: number; amount: number }>;
  nextId: number;
}

export interface GameState {
  account: AccountState;
  run: RunState;
}

/** A pristine account — no prestige history, no upgrades (a first-ever game). */
export function defaultAccount(): AccountState {
  return {
    facilityName: null,
    highWaterLegit: "none",
    highWaterCriminal: 0,
    reputationCapital: 0,
    streetKnowledge: 0,
    lifetimeGraduations: 0,
    upgrades: {},
  };
}

/**
 * A fresh RUN under an existing account (v0.4 §7). The single run-init path
 * shared by new-game AND prestige: builds the blank run, reopens the account's
 * earned tiers (high-water seeding), then applies the account's head-start
 * upgrades. `account` is NOT copied — the caller owns it (prestige mutates it
 * first, then hands it here).
 */
export function newRun(seed: number, account: AccountState): RunState {
  const tiers = {} as Record<CareTierId, TierState>;
  const zeroByTier = {} as Record<CareTierId, number>;
  const oneByTier = {} as Record<CareTierId, number>;
  for (const t of CARE_TIERS) {
    tiers[t] = { automatorLevel: 0, dischargePolicy: "standard" };
    zeroByTier[t] = 0;
    oneByTier[t] = 1;
  }
  const run: RunState = {
    tick: 0,
    rng: newRngState(seed),
    // v0.13: the event stream gets its own seed derived from the run's —
    // deterministic, but isolated (offline skips can't shift main rolls).
    events: newEventsState(seed),
    funding: ECONOMY.startingFunding,
    reputation: 0,
    heat: 0,
    dea: 0,
    office: { recruiterLevel: 0 },
    queue: [],
    // OFFICE-ONLY START (ruled): even Detox waits for the Accreditation
    // letter. High-water seeding below reopens what prior runs earned.
    availableTiers: [],
    tiers,
    patients: [],
    alumni: { roster: [], contactBoostUntil: 0, coordinatorLevel: 0, repFraction: 0 },
    graduatedOut: 0,
    graduationsByTier: { ...zeroByTier },
    issuesEver: 0,
    deathsEver: 0,
    mailbox: { arrived: [], pending: [], lastArrivalTick: -MAILBOX.minGapTicks },
    capacityBonus: { ...zeroByTier },
    capacityMultiplier: { ...oneByTier },
    markAtRunStart: account.highWaterLegit,
    prestigeOffered: false,
    peakNetPerSec: 0,
    brokerUnlocked: false,
    brokerLockoutUntil: 0,
    brokerHistory: 0,
    prFirmLevel: 0,
    complianceOfficerLevel: 0,
    fixerLevel: 0,
    deathsPending: 0,
    // criminal-tier ACCESS reopens from the account high-water mark (like legit);
    // access = deepest-reached + 1 frontier (capped). Unlocked if ever reached
    // before (§11.4); otherwise the Business Proposition unlocks it in-run.
    criminal: {
      unlocked: account.highWaterCriminal > 0,
      tierAccess: account.highWaterCriminal > 0 ? Math.min(5, account.highWaterCriminal + 1) : 0,
      upgrades: {},
    },
    purchasable: null,
    incomeLog: [],
    nextId: 1,
  };
  seedHighWater(run, account);
  applyHeadStart(run, account);
  return run;
}

/**
 * New game. Delegates to `newRun`; without an account it starts pristine.
 * `account` (when provided) drives high-water seeding + head-start.
 */
export function newGame(seed: number, account?: AccountState): GameState {
  const acct = account ?? defaultAccount();
  return { account: acct, run: newRun(seed, acct) };
}

/** Prestige-addendum seeding: rungs ≤ the mark open instantly on a new run. */
function seedHighWater(run: RunState, account: AccountState): void {
  // criminal (v0.6): if the track starts unlocked — either reached before
  // (high-water) OR the permanent "Made Man" perk — the Business Proposition is
  // pre-read so it doesn't redundantly arrive for an already-open track.
  if (account.highWaterCriminal > 0 || startsCriminalUnlocked(account)) {
    run.mailbox.arrived.push({ id: "businessProposition", arrivedTick: 0, read: true });
  }
  const mark = account.highWaterLegit;
  if (mark === "none") return;
  const order: TierId[] = [...CARE_TIERS, "alumni"];
  const upTo = order.indexOf(mark);
  for (let i = 0; i <= upTo; i++) {
    run.availableTiers.push(order[i]!);
  }
  // gate letters for seeded tiers arrive pre-read (the world doesn't re-gate).
  // welcome is the NAMING letter — only pre-read it if the facility is already
  // named, else an unnamed account (a hand-migrated save) permanently loses the
  // naming prompt (matches the v2→v3 migration's deliberate handling).
  const seeded: string[] = ["accreditation"];
  if (typeof account.facilityName === "string") seeded.unshift("welcome");
  for (let i = 1; i <= upTo; i++) {
    seeded.push(`expansionOffer-${order[i]}`);
  }
  for (const id of seeded) {
    run.mailbox.arrived.push({ id, arrivedTick: 0, read: true });
  }
}

/**
 * Head-start upgrades (v0.4 §5) applied to a fresh run: seed funding, a
 * carried-over Recruiter, and any pre-leveled tier automators. Each is clamped
 * to its real ceiling so a perk can never exceed the base cap.
 */
function applyHeadStart(run: RunState, account: AccountState): void {
  run.funding += seedFundingBonus(account);
  const recruiterMax = OFFICE.recruiterIntervalByLevel.length - 1;
  run.office.recruiterLevel = Math.min(recruiterMax, veteranRecruiterLevel(account));
  const automatorMax = AUTOMATOR_ACTIONS_BY_LEVEL.length - 1;
  for (const t of CARE_TIERS) {
    run.tiers[t].automatorLevel = Math.min(automatorMax, prelevelAutomator(account, t));
  }
  // "Made Man" (v0.6): open the criminal ledger on day one, even at zero high-water.
  if (startsCriminalUnlocked(account) && !run.criminal.unlocked) {
    run.criminal.unlocked = true;
    run.criminal.tierAccess = Math.max(1, run.criminal.tierAccess);
  }
}

/** Letter-adjusted capacity (v0.3): round(base × multiplier) + bonus. */
export function effectiveCapacity(state: GameState, tier: CareTierId): number {
  const base = TIERS[tier].capacity;
  return Math.round(base * state.run.capacityMultiplier[tier]) + state.run.capacityBonus[tier];
}

/** Progress gained per tick in a tier under its current discharge policy. */
export function progressPerTick(state: GameState, tier: CareTierId): number {
  const cfg = TIERS[tier];
  const policy = state.run.tiers[tier].dischargePolicy;
  // ticksMultiplier stretches/shrinks completion time; rate is its inverse.
  return 100 / cfg.completionTicks / policyTicksMultiplier(policy);
}

function policyTicksMultiplier(policy: DischargePolicy): number {
  return DISCHARGE[policy].ticksMultiplier;
}

/** Care tiers currently open, in order. */
export function openCareTiers(state: GameState): CareTierId[] {
  return state.run.availableTiers.filter((t): t is CareTierId => t !== "alumni");
}

/** The frontier: the LAST OPEN care tier — null before Accreditation opens
 *  Detox (office-only start, v0.3). */
export function frontierTier(state: GameState): CareTierId | null {
  const open = openCareTiers(state);
  return open.length > 0 ? open[open.length - 1]! : null;
}

export function alumniOpen(state: GameState): boolean {
  return state.run.availableTiers.includes("alumni");
}

export function patientsInTier(state: GameState, tier: CareTierId): Patient[] {
  return state.run.patients.filter((p) => p.tier === tier);
}

export function patientInSlot(state: GameState, tier: CareTierId, bed: number): Patient | undefined {
  return state.run.patients.find((p) => p.tier === tier && p.bed === bed);
}
